import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  FileText, Download, Eye, Save, RefreshCw, Plus, Trash2,
  Image, Users, Palette, Video, Globe, Megaphone, Clock,
  Layout, Building2, Phone, ChevronDown, ChevronUp, Settings,
  Copy, Link, Check
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/axios';

const CMSBrochureConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [expandedServices, setExpandedServices] = useState({});
  const [copied, setCopied] = useState(false);

  // Public brochure URL
  const publicBrochureUrl = `${window.location.origin}/site/brochure`;

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/cms/brochure-config');
      setConfig(res.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/cms/brochure-config', config);
      toast.success('Configuration sauvegardée');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleConfigUpdate = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePageUpdate = (pageName, data) => {
    setConfig(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageName]: {
          ...prev.pages[pageName],
          ...data
        }
      }
    }));
  };

  const handleServiceUpdate = (pageName, index, field, value) => {
    const services = [...config.pages[pageName].services];
    services[index] = { ...services[index], [field]: value };
    handlePageUpdate(pageName, { services });
  };

  const addService = (pageName) => {
    const services = [...(config.pages[pageName].services || [])];
    services.push({ name: '', description: '', price: '' });
    handlePageUpdate(pageName, { services });
  };

  const removeService = (pageName, index) => {
    const services = config.pages[pageName].services.filter((_, i) => i !== index);
    handlePageUpdate(pageName, { services });
  };

  const incrementVersion = async () => {
    try {
      const res = await api.post('/cms/brochure-config/increment-version');
      setConfig(prev => ({ ...prev, version: res.data.version }));
      toast.success(`Version mise à jour: ${res.data.version}`);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour de version');
    }
  };

  const downloadBrochure = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/website/brochure/download`, '_blank');
  };

  const previewBrochure = () => {
    window.open('/site/brochure', '_blank');
  };

  const copyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(publicBrochureUrl);
      setCopied(true);
      toast.success('Lien copié dans le presse-papiers');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Erreur lors de la copie');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  const pages = config?.pages || {};

  const pageIcons = {
    identity: <Building2 className="w-4 h-4" />,
    cover: <Image className="w-4 h-4" />,
    team: <Users className="w-4 h-4" />,
    graphique: <Palette className="w-4 h-4" />,
    video: <Video className="w-4 h-4" />,
    web: <Globe className="w-4 h-4" />,
    marketing: <Megaphone className="w-4 h-4" />,
    timeline: <Clock className="w-4 h-4" />,
    realisations: <Layout className="w-4 h-4" />,
    partenaires: <Building2 className="w-4 h-4" />,
    contact: <Phone className="w-4 h-4" />
  };

  const ServiceEditor = ({ pageName, services }) => (
    <div className="space-y-3 mt-4">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Services et tarifs</Label>
        <Button variant="outline" size="sm" onClick={() => addService(pageName)}>
          <Plus className="w-3 h-3 mr-1" /> Ajouter
        </Button>
      </div>
      {services?.map((service, idx) => (
        <div key={idx} className="p-3 bg-slate-50 rounded-lg space-y-2">
          <div className="flex items-center gap-2">
            <Input
              value={service.name}
              onChange={(e) => handleServiceUpdate(pageName, idx, 'name', e.target.value)}
              placeholder="Nom du service"
              className="flex-1"
            />
            <Input
              value={service.price}
              onChange={(e) => handleServiceUpdate(pageName, idx, 'price', e.target.value)}
              placeholder="Prix"
              className="w-40"
            />
            <Button variant="ghost" size="sm" onClick={() => removeService(pageName, idx)}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
          <Input
            value={service.description}
            onChange={(e) => handleServiceUpdate(pageName, idx, 'description', e.target.value)}
            placeholder="Description"
          />
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Configuration Brochure PDF</h1>
          <p className="text-slate-500">
            Modifiez le contenu de chaque page de la brochure
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="text-sm">
            Version {config?.version || '1.0'}
          </Badge>
          <Button variant="outline" size="sm" onClick={incrementVersion}>
            <RefreshCw className="w-4 h-4 mr-1" /> Version +
          </Button>
          <Button variant="outline" onClick={previewBrochure}>
            <Eye className="w-4 h-4 mr-2" /> Aperçu
          </Button>
          <Button variant="outline" onClick={downloadBrochure}>
            <Download className="w-4 h-4 mr-2" /> Télécharger
          </Button>
          <Button onClick={handleSave} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Sauvegarder
          </Button>
        </div>
      </div>

      {/* Share Link Card */}
      <Card className="border-green-200 bg-green-50/50">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Link className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-slate-900">Lien de partage public</p>
                <p className="text-sm text-slate-600 break-all">{publicBrochureUrl}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={copyPublicLink}
              className="border-green-300 hover:bg-green-100"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 mr-2 text-green-600" /> Copié !
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" /> Copier le lien
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info */}
      <Card className="border-cyan-200 bg-cyan-50/50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <FileText className="w-5 h-5 text-cyan-600 mt-0.5" />
            <div>
              <p className="font-medium text-slate-900">Brochure de 10 pages</p>
              <p className="text-sm text-slate-600">
                Modifiez les titres, descriptions, services et tarifs de chaque section. 
                Les modifications seront appliquées à la prochaine génération du PDF.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Page Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 bg-slate-100 p-1">
          <TabsTrigger 
            value="general"
            className="flex items-center gap-1.5 text-xs px-3 py-2"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Général</span>
          </TabsTrigger>
          <TabsTrigger 
            value="identity"
            className="flex items-center gap-1.5 text-xs px-3 py-2 data-[state=active]:bg-cyan-100 data-[state=active]:text-cyan-700"
          >
            <Building2 className="w-4 h-4" />
            <span className="hidden sm:inline">Identité & Contact</span>
          </TabsTrigger>
          {Object.entries(pageIcons).filter(([key]) => key !== 'identity').map(([key, icon]) => (
            <TabsTrigger 
              key={key} 
              value={key}
              className="flex items-center gap-1.5 text-xs px-3 py-2"
            >
              {icon}
              <span className="hidden sm:inline capitalize">{key === 'graphique' ? 'Graphisme' : key}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {/* General Settings Tab */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-cyan-500" />
                Paramètres généraux
              </CardTitle>
              <CardDescription>Configuration globale de la brochure PDF</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Filename */}
              <div>
                <Label>Nom du fichier PDF</Label>
                <Input
                  value={config?.filename || ''}
                  onChange={(e) => handleConfigUpdate('filename', e.target.value)}
                  placeholder="brochure-msc-agence"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Le fichier sera téléchargé sous ce nom (sans l'extension .pdf)
                </p>
              </div>

              {/* Cover background image */}
              <div>
                <Label>Image de fond de la couverture</Label>
                <Input
                  value={config?.cover_background_url || ''}
                  onChange={(e) => handleConfigUpdate('cover_background_url', e.target.value)}
                  placeholder="https://images.unsplash.com/..."
                />
                <p className="text-xs text-slate-500 mt-1">
                  URL de l'image affichée en fond sur la page de couverture
                </p>
              </div>

              {/* Sections to show/hide */}
              <div className="space-y-4">
                <Label className="text-base font-semibold">Sections à afficher</Label>
                <p className="text-sm text-slate-500">
                  Activez ou désactivez les différentes pages de la brochure
                </p>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { key: 'cover', label: 'Page de couverture', icon: '📄' },
                    { key: 'team', label: "Page de l'équipe", icon: '👥' },
                    { key: 'graphique', label: 'Création graphique', icon: '🎨' },
                    { key: 'video', label: 'Production vidéo', icon: '🎬' },
                    { key: 'web', label: 'Web & Digital', icon: '🌐' },
                    { key: 'marketing', label: 'Marketing digital', icon: '📢' },
                    { key: 'timeline', label: 'Timeline', icon: '⏱️' },
                    { key: 'realisations', label: 'Réalisations', icon: '💼' },
                    { key: 'partenaires', label: 'Partenaires', icon: '🤝' },
                    { key: 'contact', label: 'Contact', icon: '📞' },
                  ].map(({ key, label, icon }) => (
                    <div 
                      key={key}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <span>{icon}</span>
                        <span className="text-sm font-medium">{label}</span>
                      </div>
                      <Switch 
                        checked={pages[key]?.enabled !== false}
                        onCheckedChange={(checked) => handlePageUpdate(key, { enabled: checked })}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Identity & Contact Tab */}
        <TabsContent value="identity">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-cyan-500" />
                Identité & Coordonnées de la brochure
              </CardTitle>
              <CardDescription>
                Logo, coordonnées et horaires affichés dans la brochure PDF
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Logo Section */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Image className="w-4 h-4 text-cyan-500" />
                  Logo de la brochure
                </h4>
                <div>
                  <Label>URL du logo (fond clair)</Label>
                  <Input
                    value={config?.brochure_logo_url || ''}
                    onChange={(e) => handleConfigUpdate('brochure_logo_url', e.target.value)}
                    placeholder="https://... ou /api/uploads/logos/logo.png"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Logo affiché sur la couverture et les pages de la brochure
                  </p>
                </div>
                {config?.brochure_logo_url && (
                  <div className="flex items-center gap-4 p-3 bg-white rounded border">
                    <span className="text-sm text-slate-500">Aperçu :</span>
                    <img 
                      src={config.brochure_logo_url.startsWith('http') ? config.brochure_logo_url : `${process.env.REACT_APP_BACKEND_URL}${config.brochure_logo_url}`}
                      alt="Logo brochure"
                      className="h-12 w-auto object-contain"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
              </div>

              {/* Contact Info Section */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Phone className="w-4 h-4 text-cyan-500" />
                  Coordonnées
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nom de l'agence</Label>
                    <Input
                      value={config?.brochure_agency_name || ''}
                      onChange={(e) => handleConfigUpdate('brochure_agency_name', e.target.value)}
                      placeholder="MSC L'Agence"
                    />
                  </div>
                  <div>
                    <Label>Téléphone</Label>
                    <Input
                      value={config?.brochure_phone || ''}
                      onChange={(e) => handleConfigUpdate('brochure_phone', e.target.value)}
                      placeholder="04 84 49 27 00"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={config?.brochure_email || ''}
                      onChange={(e) => handleConfigUpdate('brochure_email', e.target.value)}
                      placeholder="contact@agence-msc.fr"
                    />
                  </div>
                  <div>
                    <Label>Site web</Label>
                    <Input
                      value={config?.brochure_website || ''}
                      onChange={(e) => handleConfigUpdate('brochure_website', e.target.value)}
                      placeholder="www.agence-msc.fr"
                    />
                  </div>
                </div>
                <div>
                  <Label>Adresse complète</Label>
                  <Input
                    value={config?.brochure_address || ''}
                    onChange={(e) => handleConfigUpdate('brochure_address', e.target.value)}
                    placeholder="52 Rue des Pétouses"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Code postal</Label>
                    <Input
                      value={config?.brochure_postal_code || ''}
                      onChange={(e) => handleConfigUpdate('brochure_postal_code', e.target.value)}
                      placeholder="13800"
                    />
                  </div>
                  <div>
                    <Label>Ville</Label>
                    <Input
                      value={config?.brochure_city || ''}
                      onChange={(e) => handleConfigUpdate('brochure_city', e.target.value)}
                      placeholder="ISTRES"
                    />
                  </div>
                </div>
              </div>

              {/* Schedule Section */}
              <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                <h4 className="font-semibold flex items-center gap-2">
                  <Clock className="w-4 h-4 text-cyan-500" />
                  Horaires d'ouverture
                </h4>
                <p className="text-sm text-slate-500">
                  Ces horaires seront affichés sur la dernière page de la brochure
                </p>
                <div className="space-y-3">
                  {['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'].map((day, idx) => (
                    <div key={day} className="flex items-center gap-3 p-2 bg-white rounded border">
                      <span className="w-24 font-medium text-sm">{day}</span>
                      <Input
                        value={config?.brochure_hours?.[idx]?.open || ''}
                        onChange={(e) => {
                          const hours = [...(config?.brochure_hours || Array(7).fill({ open: '', close: '' }))];
                          hours[idx] = { ...hours[idx], open: e.target.value };
                          handleConfigUpdate('brochure_hours', hours);
                        }}
                        placeholder="09h00"
                        className="w-24 text-center"
                      />
                      <span className="text-slate-400">à</span>
                      <Input
                        value={config?.brochure_hours?.[idx]?.close || ''}
                        onChange={(e) => {
                          const hours = [...(config?.brochure_hours || Array(7).fill({ open: '', close: '' }))];
                          hours[idx] = { ...hours[idx], close: e.target.value };
                          handleConfigUpdate('brochure_hours', hours);
                        }}
                        placeholder="18h00"
                        className="w-24 text-center"
                      />
                      <span className="text-xs text-slate-500 ml-2">
                        {(!config?.brochure_hours?.[idx]?.open && !config?.brochure_hours?.[idx]?.close) && 'Fermé'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cover Page */}
        <TabsContent value="cover">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Image className="w-5 h-5 text-cyan-500" />
                Page 1 - Couverture
              </CardTitle>
              <CardDescription>Page d'accueil de la brochure avec le logo et les statistiques</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Page activée</Label>
                <Switch 
                  checked={pages.cover?.enabled !== false}
                  onCheckedChange={(checked) => handlePageUpdate('cover', { enabled: checked })}
                />
              </div>
              <div>
                <Label>Titre principal</Label>
                <Input
                  value={pages.cover?.title || ''}
                  onChange={(e) => handlePageUpdate('cover', { title: e.target.value })}
                  placeholder="Boostons la visibilité de votre entreprise"
                />
              </div>
              <div>
                <Label>Sous-titre</Label>
                <Input
                  value={pages.cover?.subtitle || ''}
                  onChange={(e) => handlePageUpdate('cover', { subtitle: e.target.value })}
                  placeholder="Logos • Visuels • Vidéos • Sites web • Marketing"
                />
              </div>
              <div>
                <Label>Statistiques</Label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {pages.cover?.stats?.map((stat, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg space-y-2">
                      <Input
                        value={stat.value}
                        onChange={(e) => {
                          const stats = [...pages.cover.stats];
                          stats[idx] = { ...stat, value: e.target.value };
                          handlePageUpdate('cover', { stats });
                        }}
                        placeholder="200+"
                        className="text-center font-bold"
                      />
                      <Input
                        value={stat.label}
                        onChange={(e) => {
                          const stats = [...pages.cover.stats];
                          stats[idx] = { ...stat, label: e.target.value };
                          handlePageUpdate('cover', { stats });
                        }}
                        placeholder="Projets"
                        className="text-center text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Page */}
        <TabsContent value="team">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-cyan-500" />
                Page 2 - Équipe
              </CardTitle>
              <CardDescription>Présentation des membres de l'équipe (données venant du CMS Équipe)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Page activée</Label>
                <Switch 
                  checked={pages.team?.enabled !== false}
                  onCheckedChange={(checked) => handlePageUpdate('team', { enabled: checked })}
                />
              </div>
              <div>
                <Label>Titre</Label>
                <Input
                  value={pages.team?.title || ''}
                  onChange={(e) => handlePageUpdate('team', { title: e.target.value })}
                />
              </div>
              <div>
                <Label>Sous-titre</Label>
                <Input
                  value={pages.team?.subtitle || ''}
                  onChange={(e) => handlePageUpdate('team', { subtitle: e.target.value })}
                />
              </div>
              <div>
                <Label>Nombre max de membres</Label>
                <Input
                  type="number"
                  value={pages.team?.max_members || 6}
                  onChange={(e) => handlePageUpdate('team', { max_members: parseInt(e.target.value) })}
                  className="w-24"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Service Pages (Graphique, Video, Web, Marketing) */}
        {['graphique', 'video', 'web', 'marketing'].map((cat) => (
          <TabsContent key={cat} value={cat}>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {pageIcons[cat]}
                  Page {cat === 'graphique' ? 3 : cat === 'video' ? 4 : cat === 'web' ? 5 : 6} - {pages[cat]?.title || cat}
                </CardTitle>
                <CardDescription>Services et tarifs pour la catégorie {cat}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Page activée</Label>
                  <Switch 
                    checked={pages[cat]?.enabled !== false}
                    onCheckedChange={(checked) => handlePageUpdate(cat, { enabled: checked })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Titre</Label>
                    <Input
                      value={pages[cat]?.title || ''}
                      onChange={(e) => handlePageUpdate(cat, { title: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Icône</Label>
                    <Input
                      value={pages[cat]?.icon || ''}
                      onChange={(e) => handlePageUpdate(cat, { icon: e.target.value })}
                      placeholder="🎨"
                    />
                  </div>
                </div>
                <div>
                  <Label>Sous-titre</Label>
                  <Input
                    value={pages[cat]?.subtitle || ''}
                    onChange={(e) => handlePageUpdate(cat, { subtitle: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Nombre max de réalisations</Label>
                  <Input
                    type="number"
                    value={pages[cat]?.max_projects || 3}
                    onChange={(e) => handlePageUpdate(cat, { max_projects: parseInt(e.target.value) })}
                    className="w-24"
                  />
                </div>
                <ServiceEditor pageName={cat} services={pages[cat]?.services} />
                
                {cat === 'marketing' && pages.marketing?.copilote && (
                  <div className="mt-6 p-4 bg-orange-50 rounded-lg space-y-3">
                    <h4 className="font-semibold flex items-center gap-2">
                      🔥 COPILOTE MSC
                    </h4>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={pages.marketing.copilote.description || ''}
                        onChange={(e) => handlePageUpdate('marketing', { 
                          copilote: { ...pages.marketing.copilote, description: e.target.value }
                        })}
                        rows={2}
                      />
                    </div>
                    <div>
                      <Label>Points clés</Label>
                      <Input
                        value={pages.marketing.copilote.features || ''}
                        onChange={(e) => handlePageUpdate('marketing', { 
                          copilote: { ...pages.marketing.copilote, features: e.target.value }
                        })}
                      />
                    </div>
                    <div>
                      <Label>Prix</Label>
                      <Input
                        value={pages.marketing.copilote.price || ''}
                        onChange={(e) => handlePageUpdate('marketing', { 
                          copilote: { ...pages.marketing.copilote, price: e.target.value }
                        })}
                        className="w-40"
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}

        {/* Timeline Page */}
        <TabsContent value="timeline">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-cyan-500" />
                Page 7 - Timeline de la Com'
              </CardTitle>
              <CardDescription>Les étapes clés de la communication</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Page activée</Label>
                <Switch 
                  checked={pages.timeline?.enabled !== false}
                  onCheckedChange={(checked) => handlePageUpdate('timeline', { enabled: checked })}
                />
              </div>
              <div>
                <Label>Titre</Label>
                <Input
                  value={pages.timeline?.title || ''}
                  onChange={(e) => handlePageUpdate('timeline', { title: e.target.value })}
                />
              </div>
              <div>
                <Label>Sous-titre</Label>
                <Input
                  value={pages.timeline?.subtitle || ''}
                  onChange={(e) => handlePageUpdate('timeline', { subtitle: e.target.value })}
                />
              </div>
              <div>
                <Label>Conseil</Label>
                <Textarea
                  value={pages.timeline?.tip || ''}
                  onChange={(e) => handlePageUpdate('timeline', { tip: e.target.value })}
                  rows={2}
                />
              </div>
              <div className="space-y-2">
                <Label>Étapes (10)</Label>
                <div className="grid gap-2">
                  {pages.timeline?.steps?.map((step, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-50 rounded">
                      <Badge className="w-8 justify-center">{step.num}</Badge>
                      <Input
                        value={step.title}
                        onChange={(e) => {
                          const steps = [...pages.timeline.steps];
                          steps[idx] = { ...step, title: e.target.value };
                          handlePageUpdate('timeline', { steps });
                        }}
                        placeholder="Titre"
                        className="w-32"
                      />
                      <Input
                        value={step.description}
                        onChange={(e) => {
                          const steps = [...pages.timeline.steps];
                          steps[idx] = { ...step, description: e.target.value };
                          handlePageUpdate('timeline', { steps });
                        }}
                        placeholder="Description"
                        className="flex-1"
                      />
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Realisations Page */}
        <TabsContent value="realisations">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layout className="w-5 h-5 text-cyan-500" />
                Page 8 - Réalisations
              </CardTitle>
              <CardDescription>Grille des projets (données venant du CMS Portfolio)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Page activée</Label>
                <Switch 
                  checked={pages.realisations?.enabled !== false}
                  onCheckedChange={(checked) => handlePageUpdate('realisations', { enabled: checked })}
                />
              </div>
              <div>
                <Label>Titre</Label>
                <Input
                  value={pages.realisations?.title || ''}
                  onChange={(e) => handlePageUpdate('realisations', { title: e.target.value })}
                />
              </div>
              <div>
                <Label>Nombre max de projets</Label>
                <Input
                  type="number"
                  value={pages.realisations?.max_projects || 9}
                  onChange={(e) => handlePageUpdate('realisations', { max_projects: parseInt(e.target.value) })}
                  className="w-24"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Partners Page */}
        <TabsContent value="partenaires">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-cyan-500" />
                Page 9 - Partenaires
              </CardTitle>
              <CardDescription>Logos des clients et témoignage</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Page activée</Label>
                <Switch 
                  checked={pages.partenaires?.enabled !== false}
                  onCheckedChange={(checked) => handlePageUpdate('partenaires', { enabled: checked })}
                />
              </div>
              <div>
                <Label>Titre</Label>
                <Input
                  value={pages.partenaires?.title || ''}
                  onChange={(e) => handlePageUpdate('partenaires', { title: e.target.value })}
                />
              </div>
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <h4 className="font-medium">Témoignage client</h4>
                <div>
                  <Label>Texte</Label>
                  <Textarea
                    value={pages.partenaires?.testimonial?.text || ''}
                    onChange={(e) => handlePageUpdate('partenaires', { 
                      testimonial: { ...pages.partenaires?.testimonial, text: e.target.value }
                    })}
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Auteur</Label>
                  <Input
                    value={pages.partenaires?.testimonial?.author || ''}
                    onChange={(e) => handlePageUpdate('partenaires', { 
                      testimonial: { ...pages.partenaires?.testimonial, author: e.target.value }
                    })}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contact Page */}
        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5 text-cyan-500" />
                Page 10 - Contact & Horaires
              </CardTitle>
              <CardDescription>Page finale avec coordonnées et horaires</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Page activée</Label>
                <Switch 
                  checked={pages.contact?.enabled !== false}
                  onCheckedChange={(checked) => handlePageUpdate('contact', { enabled: checked })}
                />
              </div>
              <div>
                <Label>Titre</Label>
                <Input
                  value={pages.contact?.title || ''}
                  onChange={(e) => handlePageUpdate('contact', { title: e.target.value })}
                />
              </div>
              <div>
                <Label>Sous-titre</Label>
                <Input
                  value={pages.contact?.subtitle || ''}
                  onChange={(e) => handlePageUpdate('contact', { subtitle: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-4">
                <Label>Afficher les horaires</Label>
                <Switch 
                  checked={pages.contact?.show_hours !== false}
                  onCheckedChange={(checked) => handlePageUpdate('contact', { show_hours: checked })}
                />
              </div>
              <div>
                <Label>Texte réseaux sociaux</Label>
                <Input
                  value={pages.contact?.social_text || ''}
                  onChange={(e) => handlePageUpdate('contact', { social_text: e.target.value })}
                />
              </div>
              <div>
                <Label>Liens réseaux sociaux</Label>
                <Input
                  value={pages.contact?.social_links || ''}
                  onChange={(e) => handlePageUpdate('contact', { social_links: e.target.value })}
                  placeholder="Instagram  •  Facebook  •  LinkedIn"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Button Footer */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button variant="outline" onClick={previewBrochure}>
          <Eye className="w-4 h-4 mr-2" /> Aperçu
        </Button>
        <Button onClick={handleSave} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Sauvegarder les modifications
        </Button>
      </div>
    </div>
  );
};

export default CMSBrochureConfigPage;
